import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import Cookies from 'js-cookie'
import { baseDomain } from '../utils/api'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(() => Cookies.get('auth_token') || null)
  const [loading, setLoading] = useState(true)

  // On mount, validate existing token or fall back to legacy cookies
  useEffect(() => {
    async function init() {
      if (token) {
        try {
          const res = await fetch(`${baseDomain}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          if (res.ok) {
            const data = await res.json()
            setUser(data)
            setLoading(false)
            return
          }
        } catch (e) {
          // Token invalid, fall through
        }
        // Token invalid — clear it
        Cookies.remove('auth_token')
        setToken(null)
      }

      // Legacy fallback: check old cookie-based auth
      const cookies = Cookies.get()
      if (cookies.authenticated && cookies.user) {
        try {
          const legacyUser = typeof cookies.user === 'string' ? JSON.parse(cookies.user) : cookies.user
          setUser(legacyUser)
        } catch (e) {
          // Invalid cookie
        }
      }

      setLoading(false)
    }
    init()
  }, [])

  const login = useCallback(async (username, password) => {
    const res = await fetch(`${baseDomain}/api/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    })
    const data = await res.json()

    if (!data.authenticated) return { success: false }

    if (data.token) {
      setToken(data.token)
      Cookies.set('auth_token', data.token, { sameSite: 'strict' })
    }

    // Keep legacy cookies during transition
    Cookies.set('user', data.user)
    Cookies.set('authenticated', true)

    setUser(data.user)
    return { success: true, user: data.user }
  }, [])

  const logout = useCallback(() => {
    setToken(null)
    setUser(null)
    Cookies.remove('auth_token')
    Cookies.remove('user')
    Cookies.remove('authenticated')
    window.location.href = '/'
  }, [])

  const hasPermission = useCallback((page) => {
    if (!user) return false
    const { role, permissions } = user
    if (role === 'super_admin' || role === 'admin') return true
    if (!permissions) return false
    const pages = permissions.pages
    return pages === 'all' || (Array.isArray(pages) && pages.includes(page))
  }, [user])

  const hasRole = useCallback((...roles) => {
    if (!user) return false
    return roles.includes(user.role)
  }, [user])

  const SURVEY_PAGES = ['abstimmungen', 'konsultation', 'konsensuskonferenz']
  const isExplicit = permissions => Number(permissions?.version || 0) >= 2

  const hasSurveyAccess = useCallback((page, guidelineId) => {
    if (!user) return false
    if (user.role === 'super_admin' || user.role === 'admin') return true
    const permissions = user.permissions
    if (!permissions) return false
    if (!SURVEY_PAGES.includes(page)) return true

    const surveyAccess = permissions.survey_access?.[page]
    if (!surveyAccess) {
      if (isExplicit(permissions)) return false
      const guidelines = permissions.guidelines
      if (!guidelines || guidelines === 'all') return true
      if (Array.isArray(guidelines)) return isExplicit(permissions)
        ? Boolean(guidelineId) && guidelines.includes(guidelineId)
        : !guidelineId || guidelines.includes(guidelineId)
      return true
    }

    const guidelines = surveyAccess.guidelines
    if (!guidelines || guidelines === 'all') return true
    if (Array.isArray(guidelines)) return isExplicit(permissions)
      ? Boolean(guidelineId) && guidelines.includes(guidelineId)
      : !guidelineId || guidelines.includes(guidelineId)
    return true
  }, [user])

  const canWrite = useCallback((page) => {
    if (!user) return false
    if (user.role === 'super_admin' || user.role === 'admin') return true
    const permissions = user.permissions
    if (!permissions) return false
    if (!SURVEY_PAGES.includes(page)) return true

    const sa = permissions.survey_access?.[page]
    if (!sa) return !isExplicit(permissions)
    if (sa.actions && typeof sa.actions === 'object') {
      return Object.values(sa.actions).some(value => value === true)
    }
    return sa.access_level !== 'read'
  }, [user])

  // Granular per-action check. action ∈ { 'create' | 'edit' | 'invite' }
  // for survey pages, otherwise treats page-level access as sufficient.
  const canPerformAction = useCallback((page, action) => {
    if (!user) return false
    if (user.role === 'super_admin' || user.role === 'admin') return true
    const permissions = user.permissions
    if (!permissions) return false
    const pages = permissions.pages
    if (pages !== 'all' && !(Array.isArray(pages) && pages.includes(page))) return false
    if (!SURVEY_PAGES.includes(page)) return true

    const sa = permissions.survey_access?.[page]
    if (!sa) return !isExplicit(permissions)
    if (sa.actions && typeof sa.actions === 'object') {
      return isExplicit(permissions) ? sa.actions[action] === true : sa.actions[action] !== false
    }
    if (sa.access_level === 'read') return false
    return true
  }, [user])

  const isAuthenticated = !!user

  // Replace the stored auth token (used after server-side token rotation,
  // e.g. when the user changes their username and the backend mints a fresh
  // JWT carrying the new username in its payload).
  const setAuthToken = useCallback((newToken) => {
    if (!newToken) return
    setToken(newToken)
    Cookies.set('auth_token', newToken, { sameSite: 'strict' })
  }, [])

  // Re-fetch /me. Used after profile mutations (e.g. email change) so the UI
  // reflects pending_email without forcing a hard reload.
  const refreshUser = useCallback(async () => {
    if (!token) return null
    try {
      const res = await fetch(`${baseDomain}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setUser(data)
        return data
      }
    } catch (e) {
      // network blip; leave existing user state intact
    }
    return null
  }, [token])

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    logout,
    refreshUser,
    setAuthToken,
    hasPermission,
    hasRole,
    hasSurveyAccess,
    canWrite,
    canPerformAction,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export default AuthContext
